import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { Autocomplete, Box, CircularProgress, Divider, TextField, Typography } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import VouchersAPI from 'api/faber/VouchersAPI'
import { BootstrapDialog, BootstrapDialogTitle } from 'common/AbstractModal/BootstrapDialog'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'

interface Voucher {
  name: string;
  vendor: string;
  voucherSku: string;
}

interface AssignVoucherDialogProps {
  open: boolean
  userId: string
  onClose: () => void
}

export default function AssignVoucherDialog({
  userId,
  open,
  onClose
}: AssignVoucherDialogProps) {
  const queryClient = useQueryClient();
  const [voucherSKU, setVoucherSKU] = useState('');
  const faberToken = OrbitSession.get('faberToken');

  const { data: vouchers } = useQuery<Voucher[]>(
    ['dashboard-vouchers'],
    async () => {
      const response = await VouchersAPI.getDashboardVouchers(faberToken);
      return response.data;
    },
    {
      enabled: !!faberToken
    }
  );

  const { mutate, isLoading } = useMutation(VouchersAPI.assignCustomerVoucher, {
    onSuccess: ({data}) => {
      const voucherCode = data?.data?.voucher_code;
      toast.success(`Voucher assigned successfully. Voucher code: ${voucherCode}`)
      queryClient.refetchQueries({ queryKey: ['userVoucherData'] })
    },
    onError: () => {
      toast.error('Failed to assign customer voucher')
    },
    onSettled: () => {
      onClose()
      setVoucherSKU('');
    }
  });

  const handleAssign = () => {
    if (voucherSKU == "") {
      toast.error("Please provide voucher SKU.");
      return
    }
    
    mutate({
      faberToken,
      userId,
      voucherSKU
    });
  }

  return (
    <div>
      <BootstrapDialog
        fullWidth
        onClose={onClose}
        aria-labelledby='customized-dialog-title'
        open={open}
      >
        <BootstrapDialogTitle onClose={onClose}>
          Assign Customer Voucher
        </BootstrapDialogTitle>

        <Divider sx={{ width: '100%' }} />

        <DialogContent>
          <NormalText sx={{ p: 0 }}>
            You are about to assign a voucher to this customer. This action will grant the selected voucher to the user with ID: <b>{userId}</b>.
          </NormalText>
        </DialogContent>

        <Box sx={{ paddingX: 3, mb: 3 }}>
          <Typography sx={{ minWidth: '100px', mb: 1 }}>
            Choose a voucher to assign to this customer
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            options={vouchers ?? []}
            getOptionLabel={(option) => `${option.voucherSku} - (${option.vendor}) ${option.name}`}
            loading={false}
            onChange={(event, newValue) => setVoucherSKU(newValue?.voucherSku || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select voucher"
                variant="outlined"
              />
            )}
          />
        </Box>

        <DialogActions sx={{ mb: '1rem' }}>
          <RoundedButton onClick={onClose}>
            Cancel
          </RoundedButton>
          <LoadingButton
            loading={isLoading}
            variant='filled'
            onClick={handleAssign}
            loadingIndicator={<CircularProgress color='inherit' size={16} />}
            autoFocus
          >
            Assign
          </LoadingButton>
        </DialogActions>
      </BootstrapDialog>
    </div>
  )
}
